import { Button } from "@dropbox/dig-components/buttons";
import { Skeleton } from "@dropbox/dig-components/skeleton";
import { Snackbar } from "@dropbox/dig-components/snackbar";
import { Text } from "@dropbox/dig-components/typography";
import { UIIcon } from "@dropbox/dig-icons";
import { FailLine } from "@dropbox/dig-icons/assets";
import { AiStorageMini } from "@dropbox/dig-illustrations";
import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import { EmbeddedUpsells } from "@mirage/growth/onboarding/embedded-upsells/EmbeddedUpsells";
import { ExtensionUpsellBannerContainer } from "@mirage/growth/onboarding/extension-upsell/ExtensionUpsellBannerContainer";
import { ConversationInput } from "@mirage/mosaics/Chat/components/chat/ConversationInput";
import { ConversationMessages } from "@mirage/mosaics/Chat/components/chat/ConversationMessages/ConversationMessages";
import { useConversationToolCallsRunner } from "@mirage/mosaics/Chat/components/chat/ConversationMessages/ToolCalls/ToolCallsRunner";
import type { MarkdownResponseProps } from "@mirage/mosaics/Chat/components/chat/markdown/MarkdownResponse";
import { ConversationSuggestedQueries } from "@mirage/mosaics/Chat/components/chat/suggestions/ConversationSuggestedQueries";
import { AddSourcesModal } from "@mirage/mosaics/Chat/components/compose-sources/AddSourcesModal";
import {
  processFile,
  type FileError,
} from "@mirage/mosaics/Chat/utils/fileProcessing";
import { WorkflowAgentMessageHeader } from "@mirage/mosaics/ComposeAssistant/components/workflow-agent/WorkflowAgentMessageHeader";
import { useManuallyAddedSources } from "@mirage/mosaics/ConversationAssistant/hooks/attachments/useManuallyAddedSources";
import { DashTrialIndicatorChat } from "@mirage/mosaics/DashTrial/components/DashTrialIndicatorChat";
import { useResultsShoppingCart } from "@mirage/mosaics/ResultsShoppingCart/hooks/useResultsShoppingCart";
import { ChatTrialUpsellContainer } from "@mirage/mosaics/TrialEndingNotification/ChatTrialUpsellContainer";
import { useChatTrialUpsell } from "@mirage/mosaics/TrialEndingNotification/useChatTrialUpsell";
import {
  cancelConversationRequest,
  postToolResponses,
  postUserMessage,
} from "@mirage/service-conversation";
import useConversation from "@mirage/service-conversation/hooks/useConversation";
import type { ConversationType } from "@mirage/service-conversation/router_state_types";
import type {
  AgentId,
  ConversationMessage,
  ConversationSuggestedQuery,
  ConversationToolMessage,
  DocumentInfo,
  ModelId,
  NewUserMessage,
  UserMessageOptions,
  WorkflowAgent,
} from "@mirage/service-conversation/types";
import { convertSuggestionSourceToDocumentInfo } from "@mirage/service-conversation/utils/convertSuggestionQueryToDocumentInfo";
import { convertToChatMessage } from "@mirage/service-conversation/utils/convertToChatMessage";
import { getDashActionSurfaceComponentLoggingSource } from "@mirage/service-conversation/utils/loggingUtils";
import { useDashTrialInfo } from "@mirage/service-dash-trial/hooks/useDashTrialInfo";
import { useFeatureFlagValue } from "@mirage/service-experimentation/useFeatureFlagValue";
import { convertFeatureValueToBool } from "@mirage/service-experimentation/util";
import { getSourceUUID } from "@mirage/shared/compose/compose-session";
import { isEmbedPage } from "@mirage/shared/util/tiny-utils";
import i18n from "@mirage/translations";
import cx from "classnames";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import addSourcesStyles from "../../Chat/components/compose-sources/AddSourcesModal.module.css";
import { useFilteredMessages } from "../hooks/useFilteredMessages";
import { useFssDashChatUserInteraction } from "../hooks/useFssDashChatUserInteraction";
import styles from "./ConversationChat.module.css";

interface ConversationChatProps {
  conversationId: string;
  handleClickAdd: () => void;
  handleCloseAddSourcesModal: () => void;
  isAddSourceModalOpen: boolean;
  agent?: WorkflowAgent;
  variant?: "default" | "condensed";
  selectedModel?: ModelId;
  setSelectedModel: (model: ModelId) => void;
  startingMessageNode?: ReactNode;
  showEmptyChatInstructions?: boolean;
  messages?: ConversationMessage[];
  progressComponent?: React.ReactNode;
  isWaitingForResponse: boolean;
  agentPayload: object;
  isGeneratingResponse: boolean;
  getConversationAttachments: () => Promise<DocumentInfo[]>;
  userMessageOptions?: UserMessageOptions;
  setUserMessageOptions: (options: UserMessageOptions) => void;
  allowCustomSources?: boolean;

  /**
   * @deprecated Hidden sources are now expected to have an `inclusionType` to identify their source,
   * which will be used to determine if the sources should be hidden.
   */
  shouldHideSources?: boolean;
  showModelPickerButton: boolean;
  showContextPickerButton: boolean;
  showIncognitoButton: boolean;
  logComposeEvent: (
    event: PAPEvent,
    overrides?: {
      actionSurfaceComponent?: ActionSurfaceComponent;
    },
  ) => void;
  setIsSubmittingMessage: Dispatch<SetStateAction<boolean>>;
  overridePostUserMessage?: (message: NewUserMessage) => void;
  overrideMarkdownRenderer?: (props: MarkdownResponseProps) => React.ReactNode;
  conversationType?: ConversationType;
  showDraftGeneratedButton?: boolean;
  onToggleMobileView?: () => void;
  showFollowUpSuggestionsSkeleton?: boolean;
  hideSuggestionsForCurrentAnswer: boolean;
  setHideSuggestionsForCurrentAnswer: (hide: boolean) => void;
  shouldAutoFocusInput?: boolean;
}

export const ConversationChat = memo(
  ({
    variant = "default",
    conversationId,
    handleClickAdd,
    handleCloseAddSourcesModal,
    isAddSourceModalOpen,
    agent,
    startingMessageNode,
    showEmptyChatInstructions = false,
    agentPayload,
    messages = [],
    progressComponent,
    isWaitingForResponse,
    isGeneratingResponse,
    getConversationAttachments,
    userMessageOptions,
    setUserMessageOptions,
    logComposeEvent,
    allowCustomSources,
    shouldHideSources = false,
    showModelPickerButton = true,
    showContextPickerButton = true,
    showIncognitoButton = true,
    selectedModel,
    setSelectedModel,
    setIsSubmittingMessage,
    overridePostUserMessage,
    overrideMarkdownRenderer,
    conversationType,
    showDraftGeneratedButton,
    onToggleMobileView,
    showFollowUpSuggestionsSkeleton,
    hideSuggestionsForCurrentAnswer,
    setHideSuggestionsForCurrentAnswer,
    shouldAutoFocusInput,
  }: ConversationChatProps) => {
    const conversation = useConversation(conversationId);
    const [conversationInputHeight, setConversationInputHeight] = useState<
      number | undefined
    >(undefined);
    const [placeholderIndex, setPlaceholderIndex] = useState(0);
    const {
      manuallyAddedSources,
      setManuallyAddedSources,
      addSource,
      removeSource,
    } = useManuallyAddedSources();
    const isContextPickerEnabled = convertFeatureValueToBool(
      useFeatureFlagValue("dash_2025_05_27_assist_context_picker"),
    );
    const isIncognitoModeEnabled = convertFeatureValueToBool(
      useFeatureFlagValue("dash_2025_06_13_assist_incognito_mode"),
    );
    const filteredMessages = useFilteredMessages(conversation);
    const hideExtensionUpsell = useMemo(() => {
      // Most of upsell logic is in the extension upsell banner component/hook
      // We just want to hide banner if user is in writing mode
      const isAssistTemplateChat =
        filteredMessages.templateSelectedMessage !== undefined;
      const isWritingMode = !!conversation?.markdownDraft;
      return isAssistTemplateChat || isWritingMode;
    }, [conversation, filteredMessages]);

    // Drag and drop functionality
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const [dragError, setDragError] = useState<FileError>(null);
    const [uploadSnackbarOpen, setUploadSnackbarOpen] = useState(false);
    const dragCounter = useRef(0);
    const dragLeaveTimeout = useRef<NodeJS.Timeout>();
    const [numChatTurns, setNumChatTurns] = useState(-1); // used for upsell notifications. -1 to account for the first "What would you like to do?" message
    const { isChatTrialNotificationVisible } = useChatTrialUpsell();
    const { daysRemainingInDashTrial, adminName, displayDashTrialIndicator } =
      useDashTrialInfo();
    const { handleFssDashChatUserInteraction } = useFssDashChatUserInteraction(
      isEmbedPage(),
      conversationId,
    );

    const handlePostQuerySubmit = useCallback(
      (hideFollowUpSuggestions: boolean = false) => {
        setHideSuggestionsForCurrentAnswer(hideFollowUpSuggestions);
        handleFssDashChatUserInteraction();
      },
      [handleFssDashChatUserInteraction],
    );

    useEffect(() => {
      // isWaitingForResponse starts as true, then toggles false, so once a chat response is fully received
      // this gets toggled off and we know a turn has been completed, so increment.
      if (!isWaitingForResponse) {
        setNumChatTurns((turns) => turns + 1);
      }
    }, [isWaitingForResponse]);

    const PLACEHOLDER_TEXTS = useMemo(() => {
      const baseText = [i18n.t("conversation_prompt_placeholder")];

      // Add conditional placeholder text here based on feature flags or other conditions
      // Example: if (someFeatureEnabled && messages.length === 0) { return [...additionalPrompts, ...baseText]; }

      return baseText;
    }, [messages.length]);

    const { isActivated, cartItems, setIsActivated } = useResultsShoppingCart();
    const startingMessageNodeToDisplay = useMemo(() => {
      if (agent) {
        return <WorkflowAgentMessageHeader agent={agent} />;
      }
      return startingMessageNode;
    }, [agent, startingMessageNode]);

    // Drag and drop handlers
    const handleDragEnter = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      dragCounter.current += 1;
      if (dragLeaveTimeout.current) {
        clearTimeout(dragLeaveTimeout.current);
      }
      setIsDraggingOver(true);
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      setIsDraggingOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      dragCounter.current -= 1;
      if (dragCounter.current === 0) {
        dragLeaveTimeout.current = setTimeout(() => {
          setIsDraggingOver(false);
        }, 50);
      }
    }, []);

    const handleDrop = useCallback(
      async (e: React.DragEvent) => {
        e.preventDefault();
        dragCounter.current = 0;
        setIsDraggingOver(false);
        if (dragLeaveTimeout.current) {
          clearTimeout(dragLeaveTimeout.current);
        }
        const files = Array.from(e.dataTransfer.files);
        if (files.length === 0) return;
        const file = files[0];
        const error = await processFile(file, addSource, logComposeEvent);
        if (error) {
          setDragError(error);
        } else {
          setUploadSnackbarOpen(true);
        }
      },
      [addSource, logComposeEvent],
    );

    const composeMessages = useMemo(
      () => messages?.map(convertToChatMessage) || [],
      [messages],
    );

    const handleToolCallsCompletion = useCallback(
      (
        toolResponses: ConversationToolMessage[],
        agentId: AgentId | undefined,
      ) => {
        postToolResponses({
          messageType: "toolResponses",
          conversationId,
          model: selectedModel,
          agentPayload: JSON.stringify(agentPayload),
          agentIds: agentId ? [agentId] : userMessageOptions?.agentIds || [],
          source:
            getDashActionSurfaceComponentLoggingSource("compose_chat_pane"),
          searchParams: userMessageOptions?.searchParams,
          skipPersistence: userMessageOptions?.skipPersistence,
          toolResponses,
        });
      },
      [
        agentPayload,
        conversationId,
        selectedModel,
        userMessageOptions?.agentIds,
        userMessageOptions?.searchParams,
        userMessageOptions?.skipPersistence,
      ],
    );
    const toolCallsRunner = useConversationToolCallsRunner(
      messages,
      handleToolCallsCompletion,
    );

    const handleSubmit = useCallback(
      async (text: string, additionalAttachments?: DocumentInfo[]) => {
        setIsSubmittingMessage(true);

        const attachments = await getConversationAttachments();
        const allAttachments = [
          ...attachments,
          ...(additionalAttachments || []),
        ];

        // Cancel any ongoing request before sending a new message
        // This is handled automatically in the service layer
        cancelConversationRequest(conversationId);

        // Cancel pending tool calls -- user has decided to send a message rather than
        // confirming + running the tool calls
        const toolResponses = toolCallsRunner.cancelToolCalls();

        (overridePostUserMessage || postUserMessage)({
          conversationId,
          message: text,
          model: selectedModel,
          agentPayload: JSON.stringify(agentPayload),
          attachments: allAttachments,
          agentIds: userMessageOptions?.agentIds || [],
          source:
            getDashActionSurfaceComponentLoggingSource("compose_chat_pane"),
          searchParams: userMessageOptions?.searchParams,
          skipPersistence: userMessageOptions?.skipPersistence,
          clientContext: userMessageOptions?.clientContext,
          toolResponses,
          stackId: userMessageOptions?.stackId,
        });

        setUserMessageOptions({
          ...userMessageOptions,
          // Always reset the client context after posting it with the message
          clientContext: undefined,
          // Unless it's a workflow agent (with persistAgentIds set to true), reset the agentIds
          agentIds: !userMessageOptions?.persistAgentIds
            ? []
            : userMessageOptions?.agentIds,
        });

        // Reset any manually added sources after each message submission
        setManuallyAddedSources([]);

        // Handle any follow-up after submitting the message
        handlePostQuerySubmit?.(false);
      },
      [
        setIsSubmittingMessage,
        getConversationAttachments,
        conversationId,
        toolCallsRunner,
        overridePostUserMessage,
        selectedModel,
        agentPayload,
        userMessageOptions,
        setUserMessageOptions,
        setManuallyAddedSources,
        handlePostQuerySubmit,
      ],
    );

    const handleClickSuggestedQuery = useCallback(
      (query: ConversationSuggestedQuery) => {
        const queryAttachments = query.docIds.map(
          convertSuggestionSourceToDocumentInfo,
        );
        handleSubmit(query.text, queryAttachments);
      },
      [handleSubmit],
    );

    useEffect(() => {
      if (messages.length > 0 || PLACEHOLDER_TEXTS.length <= 1) {
        setPlaceholderIndex(0);
        return;
      }

      const interval = setInterval(() => {
        setPlaceholderIndex(
          (prevIndex) => (prevIndex + 1) % PLACEHOLDER_TEXTS.length,
        );
      }, 5000); // Change every 5 seconds

      return () => clearInterval(interval);
    }, [messages.length, PLACEHOLDER_TEXTS.length]);

    const displayMessages = composeMessages;

    useEffect(() => {
      if (isActivated) {
        setIsActivated(false);
        setManuallyAddedSources((prevSources) => {
          const existingUUIDs = new Set(prevSources.map(getSourceUUID));
          const newSources = [...cartItems]
            .filter(
              (source) =>
                source.searchResult.summarizable === "yes_summarizable",
            )
            .filter((source) => {
              const uuid = getSourceUUID(source);
              return uuid && !existingUUIDs.has(uuid);
            });
          return [...prevSources, ...newSources];
        });
      }
    }, [isActivated, setIsActivated, cartItems, setManuallyAddedSources]);

    return (
      <div
        className={cx(styles.container, {
          [styles.embedContainer]: isEmbedPage(),
          [styles.condensed]: variant === "condensed",
        })}
        {...(allowCustomSources && {
          onDragEnter: handleDragEnter,
          onDragOver: handleDragOver,
          onDragLeave: handleDragLeave,
          onDrop: handleDrop,
        })}
      >
        <div className={styles.content}>
          <ConversationMessages
            conversationId={conversation?.serverConversationId}
            variant={variant}
            messages={displayMessages}
            startingMessageNode={startingMessageNodeToDisplay}
            isWaitingForResponse={isWaitingForResponse}
            isGeneratingResponse={isGeneratingResponse}
            isChatTrialNotificationVisible={isChatTrialNotificationVisible}
            progressComponent={progressComponent}
            onRemoveSource={removeSource}
            isLastMessageFollowUpSuggestions={false} // do i need this?
            artifacts={[]}
            logComposeEvent={logComposeEvent}
            onOpenArtifact={onToggleMobileView || (() => {})}
            userMessageOptions={userMessageOptions}
            footer={
              showFollowUpSuggestionsSkeleton ? (
                <div className={styles.suggestedQueriesSkeletonContainer}>
                  {Array.from({ length: 3 }).map((_, index) => (
                    <Skeleton.Box
                      key={index}
                      height={28}
                      withBorderRadius="xlarge"
                      className={styles.suggestedQueriesSkeleton}
                    />
                  ))}
                </div>
              ) : (
                conversation?.suggestedQueries &&
                conversation?.suggestedQueries.length > 0 &&
                !isWaitingForResponse &&
                !userMessageOptions?.hideFollowUpSuggestions &&
                !hideSuggestionsForCurrentAnswer && (
                  <ConversationSuggestedQueries
                    suggestedQueries={conversation.suggestedQueries}
                    onSubmit={handleClickSuggestedQuery}
                    logComposeEvent={logComposeEvent}
                    conversationId={conversation?.serverConversationId}
                    assistRequestId={conversation?.assistRequestId}
                  />
                )
              )
            }
            fullHeight={showEmptyChatInstructions}
            hideSources={shouldHideSources}
            assistRequestId={conversation?.assistRequestId}
            spanId={conversation?.spanId}
            conversationInputHeight={conversationInputHeight}
            overrideMarkdownRenderer={overrideMarkdownRenderer}
            conversationType={conversationType}
            showDraftGeneratedButton={showDraftGeneratedButton}
            toolCallsRunner={toolCallsRunner}
          />
          {!isEmbedPage() && !hideExtensionUpsell && (
            <ExtensionUpsellBannerContainer />
          )}
          {isEmbedPage() && <EmbeddedUpsells />}
          <ChatTrialUpsellContainer numChatTurns={numChatTurns} />
          <ConversationInput
            variant={variant}
            sources={manuallyAddedSources}
            onSubmit={handleSubmit}
            logComposeEvent={logComposeEvent}
            messages={composeMessages}
            placeholderOverride={PLACEHOLDER_TEXTS[placeholderIndex]}
            handleClickAdd={handleClickAdd}
            selectedModel={selectedModel}
            setSelectedModel={setSelectedModel}
            showAddSourcesButton={allowCustomSources}
            conversationId={conversation?.serverConversationId}
            assistRequestId={conversation?.assistRequestId}
            showModelPickerButton={showModelPickerButton}
            showContextPickerButton={
              isContextPickerEnabled && showContextPickerButton
            }
            showIncognitoButton={isIncognitoModeEnabled && showIncognitoButton}
            userMessageOptions={userMessageOptions}
            setUserMessageOptions={setUserMessageOptions}
            isWorkflowAgentPreview={agent?.id === "preview"}
            onRemoveSource={removeSource}
            onHeightChange={setConversationInputHeight}
            shouldAutoFocusInput={shouldAutoFocusInput}
          />
          {isEmbedPage() && displayDashTrialIndicator && (
            <div className={styles.dashTrialSection}>
              <DashTrialIndicatorChat
                daysLeft={daysRemainingInDashTrial}
                adminName={adminName}
              />
            </div>
          )}
          {isAddSourceModalOpen && (
            <AddSourcesModal
              sources={manuallyAddedSources}
              addSource={addSource}
              removeSource={removeSource}
              onRequestClose={handleCloseAddSourcesModal}
              logComposeEvent={logComposeEvent}
            />
          )}
          {uploadSnackbarOpen && (
            <Snackbar.Position>
              <Snackbar
                open
                preferComposition
                timeout={5000}
                onRequestClose={() => {
                  setUploadSnackbarOpen(false);
                }}
              >
                <Snackbar.Content>
                  <Snackbar.Message>
                    {i18n.t("compose_assistant_file_uploader_snackbar_message")}
                  </Snackbar.Message>
                  <Snackbar.Actions>
                    <Button
                      variant="transparent"
                      size="medium"
                      tone="neutral"
                      onClick={() => setUploadSnackbarOpen(false)}
                    >
                      {i18n.t(
                        "compose_assistant_file_uploader_snackbar_dismiss",
                      )}
                    </Button>
                  </Snackbar.Actions>
                </Snackbar.Content>
              </Snackbar>
            </Snackbar.Position>
          )}
          {dragError && (
            <Snackbar.Position>
              <Snackbar
                open
                preferComposition
                timeout={5000}
                onRequestClose={() => {
                  setDragError(null);
                }}
              >
                <Snackbar.Content>
                  <Snackbar.Accessory>
                    <UIIcon src={FailLine} />
                  </Snackbar.Accessory>
                  <Snackbar.Message>
                    {i18n.t("compose_assistant_file_uploader_error_title")}
                    &nbsp;
                    {dragError === "size"
                      ? i18n.t("compose_assistant_file_uploader_error_size")
                      : dragError === "type"
                        ? i18n.t("compose_assistant_file_uploader_error_type")
                        : i18n.t(
                            "compose_assistant_file_uploader_error_unexpected",
                          )}
                  </Snackbar.Message>
                  <Snackbar.Actions>
                    <Button
                      variant="transparent"
                      size="medium"
                      tone="neutral"
                      onClick={() => setDragError(null)}
                    >
                      {i18n.t("dismiss")}
                    </Button>
                  </Snackbar.Actions>
                </Snackbar.Content>
              </Snackbar>
            </Snackbar.Position>
          )}
          {allowCustomSources && isDraggingOver && (
            <div className={addSourcesStyles.AddSourcesModalDragOverlay}>
              <AiStorageMini
                altText=""
                className={addSourcesStyles.AddSourcesModalDragIcon}
              />
              <Text className={addSourcesStyles.AddSourcesModalDragTitle}>
                {i18n.t("compose_assistant_file_uploader_dnd_title")}
              </Text>
              <Text className={addSourcesStyles.AddSourcesModalDragDescription}>
                {i18n.t("compose_assistant_file_uploader_dnd_subtitle")}
              </Text>
              <Text className={addSourcesStyles.AddSourcesModalDragSubtext}>
                {i18n.t("compose_assistant_file_uploader_dnd_text")}
              </Text>
            </div>
          )}
        </div>
      </div>
    );
  },
);

ConversationChat.displayName = "ConversationChat";
